import BasePaymentController from 'app/src/controllers/payments/BasePaymentController';
import CommonData from 'app/src/data/CommonData';
import PaymentListView from 'app/src/views/payments/PaymentListView';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentProvider from 'app/src/payments/PaymentProvider';
import GiftCardPayment from 'app/src/payments/GiftCardPayment';
import TerminalPayment from 'app/src/payments/TerminalPayment';
import SwipePayment from 'app/src/payments/SwipePayment';
import DepositPayment from 'app/src/payments/DepositPayment';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import PaymentTransaction from 'app/src/data/models/PaymentTransaction';
import DepositManager from 'app/src/payments/DepositManager';
import PaymentAnalytics from 'app/src/payments/PaymentAnalytics';
import PaymentOperations from 'app/src/payments/PaymentOperations';
import GatewayConfig from 'app/src/data/models/GatewayConfig';
import Localize from 'app/src/localize/Localize';
import RetailManager from 'app/retail_manager/RetailManager';
import SaleManager from 'app/src/payments/SaleManager';
import Payment from 'app/src/data/models/Payment';
import Monitoring from 'shared_modules/Monitoring';

import TransactionResultModal from 'payments/components/TransactionResultModal';
import TerminalPaymentModal from 'payments/TerminalPayment';
import { createReactRenderer } from 'helpers/react-renderer';

import get from 'lodash/get';

export default class PaymentListViewController extends BasePaymentController {
  constructor(saleManager, paymentProvider) {
    if (!(paymentProvider instanceof PaymentProvider)) {
      throw 'A payment provider must be provided.';
    }

    super(saleManager);

    this._paymentProvider = paymentProvider;

    // payments that may be listed include bank and gift card payments
    this._salePayments = this._saleManager.creditDebitPayments;
    this._salePayments.add(this._saleManager.giftCardPayments.models); // merge the collections

    this._addDepositRefunds();

    let hideRemoveButton = false;
    if (
      this._paymentProvider.activeGateway &&
      this._paymentProvider.activeGateway.get('gatewayID') === GatewayConfig.GatewayType.TYRO
    ) {
      hideRemoveButton = true;
    }

    this.view = new PaymentListView({
      collection: this._salePayments,
      isRefundSale: this._saleManager.isRefundSale(),
      hideRemoveButton,
      disableRemoveButton: !this._paymentProvider.activeGateway,
    });
  }

  /**
   * Shows the view managed by the controller.
   */
  show() {
    super.show();

    this.listenTo(
      RetailManager.events,
      RetailManager.Event.REFUND_COMPLETE,
      this._onRefundComplete
    );
    this.listenTo(this._saleManager, SaleManager.Events.SALE_REFRESHED, this._refreshPayments);
    this.listenTo(
      this._saleManager.deposits,
      DepositManager.Events.DEPOSITS_CHANGED,
      this._addDepositRefunds
    );
    this.listenTo(
      this._saleManager,
      SaleManager.Events.COMPLETE_SALE_STARTED,
      this._onCompleteSaleStarted
    );
    this.listenTo(
      this._saleManager,
      SaleManager.Events.COMPLETE_SALE_FAILED,
      this._onCompleteSaleFailed
    );
  }

  /**
   * Event handler for when a remove button is clicked from within the list.
   *
   * @param {PaymentListPaymentView} paymentView  The view of the payment which triggered the event.
   */
  onPaymentRemoveButtonClicked(paymentView) {
    const supportMessage = `Try again and if the problem persists, contact <a class="strong-link" target="_blank" href="https://www.lightspeedhq.com/support/retail">Support</a>`;

    let payment = null;
    if (paymentView.model.depositPayment) {
      // re-build the deposit payment
      payment = PaymentFactory.createPayment(PaymentTypeManager.Types.DEPOSIT);
      payment.model = paymentView.model;
      payment.depositPayment = paymentView.model.depositPayment;
    } else {
      payment = PaymentFactory.createPaymentFromModel(paymentView.model);
    }

    if (payment instanceof TerminalPayment) {
      PaymentAnalytics.trackPaymentEvent('Remove', this._saleManager.sale.id, {
        salePaymentID: payment.model.id,
        paymentProcessor: this._paymentProvider.activeGatewayProcessorType,
      });

      // Since we're removing a payment from an unfinished sale we want to fully revert
      // the payment including the tipAmount
      payment.refundTip = true;

      // should be new terminals that utilize the payment service so make sure we have a payment ID
      if (payment.isServicePayment()) {
        const tipAmount = payment.model.get('tipAmount') || null;
        const cardType = get(payment, 'model.details.cardType');

        if (cardType === PaymentTransaction.SupportedCardType.INTERAC) {
          const refundableAmount = payment.model.get('amount').negated();
          const refundedPaymentID = payment.model.get('paymentID');
          const terminal = this._paymentProvider.getTerminal(
            CommonData.selectedRegister.get('ccTerminalID')
          );

          if (refundableAmount && refundedPaymentID && terminal) {
            TerminalPaymentModal.doTerminalPayment({
              initialAmount: refundableAmount,
              terminal,
              saleManager: this._saleManager,
              paymentProvider: this._paymentProvider,
              isManualEntry: false,
              isUnreferencedRefund: false,
              refundedPaymentID,
              ...(tipAmount && { tipAmount: tipAmount.negated() }),
            });
          } else {
            if (!refundableAmount || !refundedPaymentID) {
              alertUser(Localize.translate(`Unable to void payment. ${supportMessage}`));
            }

            if (!terminal) {
              alertUser(
                Localize.translate(
                  `Unable to find the terminal used for this transaction. Finish this sale, then refund it.`
                )
              );
            }
          }
        } else {
          PaymentOperations.doVoidOperation(payment, this._saleManager, this._paymentProvider)
            .done(response => {
              if (!RetailManager.active) {
                this._salePayments.remove(paymentView.model);
              }

              if (
                this._paymentProvider.activeGateway.get('gatewayID') ===
                  GatewayConfig.GatewayType.VANTIV_TRIPOS ||
                this._paymentProvider.activeGateway.get('gatewayID') ===
                  GatewayConfig.GatewayType.LIGHTSPEED_PAYMENTS
              ) {
                const message =
                  response.serviceModel &&
                  response.serviceModel.paymentTransaction &&
                  response.serviceModel.paymentTransaction.parameters &&
                  response.serviceModel.paymentTransaction.parameters.message
                    ? response.serviceModel.paymentTransaction.parameters.message
                    : response.error;

                const detailedMessage =
                  response.serviceModel &&
                  response.serviceModel.paymentTransaction &&
                  response.serviceModel.paymentTransaction.parameters &&
                  response.serviceModel.paymentTransaction.parameters.detailedMessage
                    ? response.serviceModel.paymentTransaction.parameters.detailedMessage
                    : null;
                const RenderedComponent = createReactRenderer({
                  component: TransactionResultModal,
                  selector: '[data-react-root="ReactModal"]',
                  props: {
                    onHide: () => {
                      delete this.TransactionResultModal;
                      RenderedComponent.unmount();
                    },
                    amount: response.model.get('amount'),
                    message,
                    detailedMessage,
                    paymentDetails: {
                      cardBrand: get(response, 'model.details.cardBrand', 'Unknown'),
                      maskedDigits: get(response, 'model.details.maskedDigits', ''),
                    },
                    salePaymentID: response.serviceModel
                      .get('SalePayment')
                      .salePaymentID.toString(),
                    status: Payment.Status.VOIDED,
                    systemCustomerID: window.merchantos.session.getSystemCustomerID(),
                  },
                });

                this.TransactionResultModal = RenderedComponent.mount();
              }
            })
            .fail(() => {
              alertUser(Localize.translate(`Unable to void payment. ${supportMessage}`));
            });
        }
      } else if (
        RetailManager.active &&
        this._paymentProvider.activeGatewaySupportedTerminals &&
        payment.model.transactionReferenceID
      ) {
        // The payment has a reference ID so it must be removed in a special way.
        // This can only happen in Retail Manager.
        RetailManager.cardReader.initiatePaymentTerminalRefund(
          this._saleManager.sale.id.toString(),
          payment.model.transactionReferenceID,
          payment.amount.toString(),
          this._saleManager.pendingBalance.toString()
        );
      } else {
        this._saleManager
          .removePayment(payment)
          .done(() => {
            this._salePayments.remove(paymentView.model);
          })
          .fail(errorMessage => {
            Monitoring.NRError(
              new Error(`Merchant received an error while removing a payment from the sale`),
              'PaymentListViewController',
              {
                reason: errorMessage,
              }
            );

            alertUser(Localize.translate(`Unable to remove payment. ${supportMessage}`));
          });
      }
    } else if (payment instanceof GiftCardPayment || payment instanceof SwipePayment) {
      this._saleManager
        .removePayment(payment)
        .done(() => {
          this._salePayments.remove(paymentView.model);
        })
        .fail(errorMessage => {
          Monitoring.NRError(
            new Error(
              `Merchant received an error while removing a giftcard or a swipe payment in a sale`
            ),
            'PaymentListViewController',
            {
              reason: errorMessage,
            }
          );

          alertUser(Localize.translate(`Unable to remove payment. ${supportMessage}`));
        });
    } else if (payment instanceof DepositPayment) {
      this._saleManager.deposits.removeDepositRefund(payment);
      this._salePayments.remove(paymentView.model);
    } else {
      // We reach this case when the payment type is no-longer accessible.
      // If the Payment Service goes down this could be "user defined" or
      // any other type that won't match any of the following types:
      // [TerminalPayment, GiftCardPayment, SwipePayment, DepositPayment]
      Monitoring.NRError(
        new Error(`Merchant received an error while removing a payment in a sale`),
        'PaymentListViewController',
        {
          reason: `The payment type "${payment.constructor.name}" is not an instance of [TerminalPayment, GiftCardPayment, SwipePayment, DepositPayment]`,
        }
      );

      alertUser(Localize.translate(`Unable to remove payment. ${supportMessage}`));
    }
  }

  /**
   * Event handler for when Retail Manager indicates that refund has been completed.
   *
   * @param {object} refundDetails
   * @param {string} refundDetails.transactionReferenceID   The original transaction reference ID
   * @param {string} [refundDetails.salePaymentID]  The ID of the original payment that was refunded
   * @private
   */
  _onRefundComplete(refundDetails) {
    let removedPayment = this._salePayments.find(
      payment =>
        (refundDetails.transactionReferenceID &&
          payment.transactionReferenceID === refundDetails.transactionReferenceID) ||
        (refundDetails.salePaymentID && payment.id === refundDetails.salePaymentID)
    );

    if (removedPayment) {
      let payment = PaymentFactory.createPaymentFromModel(removedPayment);

      this._saleManager
        .removePayment(payment)
        .done(() => {
          this._salePayments.remove(removedPayment);
        })
        .fail(() => {
          alertUser(Localize.translate(`Unable to remove payment. ${supportMessage}`));
        });
    } else {
      // It could be that the payment was removed with terminal interaction.  Find a matching reference.
      let servicePayment = this._saleManager.getServicePaymentByGatewayRef(
        refundDetails.transactionReferenceID
      );
      if (servicePayment) {
        removedPayment = this._salePayments.find(
          payment => payment.get('paymentID') === servicePayment.id
        );

        if (removedPayment) {
          this._salePayments.remove(removedPayment);
        }
      }
    }
  }

  /**
   * Adds any deposit refund payments to the payment list
   * @private
   */
  _addDepositRefunds() {
    // It's a good thing the payment UI is completely changing because this view needs a re-think.
    // Anyway, we're adding the deposit refunds to the list of payments so we can display them
    // in the payment list.
    let depositRefunds = this._saleManager.deposits.depositRefunds;
    _.each(depositRefunds, depositRefund => {
      depositRefund.model.depositPayment = depositRefund.depositPayment;
      this._salePayments.add(depositRefund.model);
    });
  }

  /**
   * Refreshes the credit payments with the most up-to-date data.
   *
   * @private
   */
  _refreshPayments() {
    this._salePayments.reset(this._saleManager.creditDebitPayments.models);
    this._salePayments.add(this._saleManager.giftCardPayments.models); // merge the collections

    this._addDepositRefunds();
  }

  /**
   * Event handler for when a sale has started the completion process
   */
  _onCompleteSaleStarted() {
    // force all remove (i.e. void) buttons to be disabled to prevent voiding while a sale is completing
    this.view.options.disableRemoveButton = true;
    this._refreshPayments();
  }

  /**
   * Event handler for when a sale has failed to complete
   */
  _onCompleteSaleFailed() {
    // re-enable all remove (i.e. void) buttons
    if (this.view.options.disableRemoveButton) {
      delete this.view.options.disableRemoveButton;
    }

    this._refreshPayments();
  }
}
